package org.example.project.service

import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import kotlinx.browser.window
import kotlinx.coroutines.await
import org.example.project.manager.LumaticaApiManager
import org.example.project.manager.LumaticaApiSettings
import org.example.project.enginetabular.loadWithTabularFilters
import org.example.project.utils.All
import org.w3c.fetch.Headers
import org.w3c.fetch.RequestInit

object InfrastrukturApi {
    private const val EMPTY_FIELD = "~"

    private val json = Json {
        ignoreUnknownKeys = true
        encodeDefaults = false
    }

    private fun headers() = LumaticaApiManager.auditHeaders("application/json")

    suspend fun loadInfra(): All? =
        decodeOne(call("infra"))

    suspend fun saveInfra(data: All): All? {
        val response = window.fetch(
            "${LumaticaApiSettings.getBaseUrl()}/api/infrastruktur/infra-save",
            RequestInit(
                method = "POST",
                headers = headers(),
                body = json.encodeToString(All.AllNames.serializer(), data)
            )
        ).await()
        if (!response.ok) return null
        return decodeOne(response.text().await())
    }

    suspend fun loadLhmTypen(): List<All> =
        decodeList(call("lhmtypen"))

    suspend fun loadLhmRegister(): List<All> =
        decodeList(call("lhmregister"))

    suspend fun saveLhmTyp(data: All): All? =
        decodeOne(call("lhmtyp-save", data.id, data.bezeichnung, data.kurzbezeichnung))

    suspend fun saveLhmRegister(data: All): All? =
        decodeOne(call("lhmregister-save", data.id, data.bezeichnung, data.kurzbezeichnung))

    suspend fun deleteLhmTyp(id: String): Boolean =
        decodeBool(call("lhmtyp-delete", id))

    suspend fun loadModulTypen(): List<All> =
        decodeList(call("modultypen"))

    suspend fun saveModulTyp(data: All): All? =
        decodeOne(call("modultyp-save", data.id, data.modulTyp, data.bezeichnung, data.vpCCG1, data.vpCCG2))

    suspend fun deleteModulTyp(id: String): Boolean =
        decodeBool(call("modultyp-delete", id))

    suspend fun loadTore(firmenId: String = ""): List<All> =
        decodeList(callScoped(firmenId, "tore"))

    suspend fun loadToreFiltered(filters: All): List<All> {
        val response = window.fetch(
            "${LumaticaApiSettings.getBaseUrl()}/api/infrastruktur/tore-filter",
            RequestInit(
                method = "POST",
                headers = headers(),
                body = json.encodeToString(All.AllNames.serializer(), filters)
            )
        ).await()
        if (!response.ok) return emptyList()
        return decodeList(response.text().await())
    }

    suspend fun searchTore(filters: Map<String, String>): List<All> =
        decodeList(loadWithTabularFilters("infrastruktur/tore-tabular", filters))

    suspend fun saveTor(data: All, firmenId: String = ""): All? {
        val saved = decodeOne(
            callScoped(
                firmenId,
                "tor-save",
                data.id,
                data.standort,
                data.halle,
                data.tor?.toString().orEmpty(),
                data.torcode.orEmpty(),
                data.belegt.toString(),
                (data.gesperrt == true).toString(),
                "",
                "",
                data.bereich
            )
        )
        if (saved != null) {
            val torPart = saved.tor?.toString().orEmpty().ifBlank { data.tor?.toString().orEmpty() }
            val standortPart = saved.standort.ifBlank { data.standort }
            val hallePart = saved.halle.ifBlank { data.halle }
            if (standortPart.isNotBlank() && hallePart.isNotBlank() && torPart.isNotBlank()) {
                GlobalManager.markJsonChanged(
                    "tor_${torPart}.json",
                    "standorte/$standortPart/hallen/$hallePart/tor_$torPart.json"
                )
            } else {
                GlobalManager.markJsonChanged("yard", "yard/tor.json")
            }
        }
        return saved
    }

    suspend fun saveTore(data: List<All>): List<All> {
        if (data.isEmpty()) return emptyList()
        val response = window.fetch(
            "${LumaticaApiSettings.getBaseUrl()}/api/infrastruktur/tor-save-batch",
            RequestInit(
                method = "POST",
                headers = headers(),
                body = json.encodeToString(ListSerializer(All.AllNames.serializer()), data)
            )
        ).await()
        if (!response.ok) return emptyList()
        return decodeList(response.text().await())
    }

    suspend fun deleteTor(id: String): Boolean =
        decodeBool(call("tor-delete", id))

    suspend fun deleteTore(data: List<All>): Boolean {
        if (data.isEmpty()) return true
        val response = window.fetch(
            "${LumaticaApiSettings.getBaseUrl()}/api/infrastruktur/tor-delete-batch",
            RequestInit(
                method = "POST",
                headers = headers(),
                body = json.encodeToString(ListSerializer(All.AllNames.serializer()), data)
            )
        ).await()
        if (!response.ok) return false
        return decodeBool(response.text().await())
    }

    suspend fun loadParkplaetze(firmenId: String = ""): List<All> =
        decodeList(callScoped(firmenId, "parkplaetze"))

    suspend fun searchParkplaetze(filters: Map<String, String>): List<All> =
        decodeList(loadWithTabularFilters("infrastruktur/parkplaetze-tabular", filters))

    suspend fun searchParkplatzTore(filters: Map<String, String>): List<All> =
        decodeList(loadWithTabularFilters("infrastruktur/parkplatz-tore-tabular", filters))

    suspend fun searchPlaetze(typ: String, filters: Map<String, String>): List<All> =
        decodeList(
            loadWithTabularFilters(
                "infrastruktur/plaetze-tabular",
                filters,
                mapOf("typ" to typ)
            )
        )

    suspend fun saveParkplatz(data: All, firmenId: String = ""): All? =
        decodeOne(
            callScoped(
                firmenId,
                "parkplatz-save",
                data.id,
                data.standort,
                data.platz,
                data.belegt.toString(),
                (data.gesperrt == true).toString(),
                data.tore.joinToString(",")
            )
        )

    suspend fun saveParkplaetze(data: List<All>, firmenId: String = ""): List<All> {
        if (data.isEmpty()) return emptyList()
        val route = if (firmenId.trim().isBlank()) {
            "infrastruktur/parkplatz-save-batch"
        } else {
            buildList {
                add("infrastruktur")
                add("company-parkplatz-save-batch")
                add(firmenId.trim())
            }.joinToString("/") { apiPart(it) }
        }
        val response = window.fetch(
            "${LumaticaApiSettings.getBaseUrl()}/api/$route",
            RequestInit(
                method = "POST",
                headers = headers(),
                body = json.encodeToString(ListSerializer(All.AllNames.serializer()), data)
            )
        ).await()
        if (!response.ok) return emptyList()
        return decodeList(response.text().await())
    }

    suspend fun deleteParkplatz(standort: String, id: String, firmenId: String = ""): Boolean =
        decodeBool(callScoped(firmenId, "parkplatz-delete", standort, id))

    suspend fun loadPlaetze(): List<All> =
        decodeList(call("plaetze"))

    suspend fun loadPlaetzeFiltered(filters: All): List<All> {
        val response = window.fetch(
            "${LumaticaApiSettings.getBaseUrl()}/api/infrastruktur/plaetze-filter",
            RequestInit(
                method = "POST",
                headers = headers(),
                body = json.encodeToString(All.AllNames.serializer(), filters)
            )
        ).await()
        if (!response.ok) return emptyList()
        return decodeList(response.text().await())
    }

    suspend fun savePlatz(data: All): All? =
        decodeOne(
            call(
                "platz-save",
                data.id,
                data.typ,
                data.standort,
                data.halle,
                data.tor?.toString().orEmpty(),
                data.torcode.orEmpty(),
                data.regal,
                data.fach,
                data.ebene,
                data.platzcode,
                data.modulTyp,
                data.belegtTyp,
                data.vpCCG1,
                data.vpCCG2,
                data.belegt.toString(),
                data.aktiv.toString(),
                data.tore.joinToString(","),
                ""
            )
        )

    suspend fun savePlaetze(data: List<All>): List<All> {
        if (data.isEmpty()) return emptyList()
        val response = window.fetch(
            "${LumaticaApiSettings.getBaseUrl()}/api/infrastruktur/platz-save-batch",
            RequestInit(
                method = "POST",
                headers = headers(),
                body = json.encodeToString(ListSerializer(All.AllNames.serializer()), data)
            )
        ).await()
        if (!response.ok) return emptyList()
        return decodeList(response.text().await())
    }

    suspend fun deletePlatz(data: All): Boolean {
        val response = window.fetch(
            "${LumaticaApiSettings.getBaseUrl()}/api/infrastruktur/platz-delete",
            RequestInit(
                method = "POST",
                headers = headers(),
                body = json.encodeToString(All.AllNames.serializer(), data)
            )
        ).await()
        if (!response.ok) return false
        return decodeBool(response.text().await())
    }

    suspend fun deletePlaetze(data: List<All>): Boolean {
        if (data.isEmpty()) return true
        val response = window.fetch(
            "${LumaticaApiSettings.getBaseUrl()}/api/infrastruktur/platz-delete-batch",
            RequestInit(
                method = "POST",
                headers = headers(),
                body = json.encodeToString(ListSerializer(All.AllNames.serializer()), data)
            )
        ).await()
        if (!response.ok) return false
        return decodeBool(response.text().await())
    }

    private suspend fun call(action: String, vararg parts: String): String {
        val route = buildList {
            add("infrastruktur")
            add(action)
            parts.forEach { add(it) }
        }.joinToString("/") { apiPart(it) }
        return LumaticaApiManager.call(route)
    }

    private suspend fun callScoped(firmenId: String, action: String, vararg parts: String): String {
        val normalizedFirmenId = firmenId.trim()
        return if (normalizedFirmenId.isBlank()) {
            call(action, *parts)
        } else {
            val route = buildList {
                add("infrastruktur")
                add("company-$action")
                add(normalizedFirmenId)
                parts.forEach { add(it) }
            }.joinToString("/") { apiPart(it) }
            LumaticaApiManager.call(route)
        }
    }

    private fun decodeOne(raw: String): All? =
        runCatching { json.decodeFromString(All.AllNames.serializer(), raw) }.getOrNull()

    private fun decodeList(raw: String): List<All> {
        if (!isJsonArray(raw)) return emptyList()
        return runCatching { json.decodeFromString(ListSerializer(All.AllNames.serializer()), raw) }.getOrDefault(emptyList())
    }

    private fun decodeBool(raw: String): Boolean =
        raw.contains("\"ok\":true")

    private fun apiPart(value: String): String =
        js("encodeURIComponent")(if (value.isBlank()) "~" else value) as String

    private fun isJsonArray(raw: String): Boolean =
        runCatching {
            val data = js("JSON.parse")(raw)
            val isArray = js("Array.isArray")(data) as Boolean
            isArray
        }.getOrDefault(false)
}
